import React, {Component} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {connect} from "react-redux";
import {compose} from "recompose";
import {
    addCommas,
    addFromColumn,
    parseCurrency,
    sortCurrency,
    toCurrency,
} from '../shared/functions';
import {getGetHeaders} from '../shared/serverFunctions';
import {setDefaultTableSort, getTableState} from '../shared/tableFunctions';
import {checkTokenStatus} from '../shared/tokenFunctions';
import ReactTable from 'react-table';
import {CSVLink} from 'react-csv';
import {RESOURCES as reportsResources} from '../fuseReportsResourceMap';
import Config from '../messages';
import {commonStyles} from '../shared/styles';
import {tableStyles} from '../shared/styles-table';
import FlatButton from "material-ui/FlatButton";
import ExportIcon from 'material-ui/svg-icons/navigation/arrow-downward';
import SalesSummaryIcon from 'material-ui/svg-icons/editor/attach-money';
import '../shared/react-table.css';

/**
 * Daily Sales by Day class for retrieving and displaying Daily Sales Summary
 *
 */
class DailySalesByDayReport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            salesSummaryList: [],
            message: null,
            reportName: 'Daily Sales',
            salesDataCash: {},
            salesDataCreditCard: {},
            salesDataOOS: {},
            salesDataPoints: {},
            summaryComplete: false,
            tableData: [],
            tableHeaders: [
                {label: 'Date', key: 'date'},
                {label: 'Payment', key: 'payment'},
                {label: 'Total Payments', key: 'totalPayments'},
                {label: 'Total Returns', key: 'totalReturns'},
                {label: 'Sale Total', key: 'saleTotal'},
                {label: 'Sale Subtotal', key: 'saleSubtotal'},
                {label: 'Sale Tax', key: 'saleTax'},
                {label: 'Return Total', key: 'returnTotal'},
                {label: 'Return Subtotal', key: 'returnSubtotal'},
                {label: 'Return Tax', key: 'returnTax'},
                {label: 'Adj. Net', key: 'net'},
            ],
        };
        this.handleExportClick = this.handleExportClick.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.handleSortChange = this.handleSortChange.bind(this);
    }

    componentWillMount() {
        this.makeSalesDataCalls();
    }

    componentWillReceiveProps(nextProps) {
        let clearTable = false;
        if (
            nextProps.locationId !== this.props.locationId ||
            nextProps.day !== this.props.day ||
            nextProps.offset !== this.props.offset
        ) {
            clearTable = true;
        }
        if (clearTable) {
            this.setState({
                salesSummaryList: [],
                salesDataCash: {},
                salesDataCreditCard: {},
                salesDataOOS: {},
                salesDataPoints: {},
                summaryComplete: false
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.locationId !== this.props.locationId ||
            prevProps.day !== this.props.day ||
            prevProps.offset !== this.props.offset
        ) {
            this.makeSalesDataCalls();
        }
    }

    /* API Calls ------------------------------ */

    /**
     * Set up new API calls for transaction and type
     */
    makeSalesDataCalls() {

        // Get Payments: Cash and Credit Card (Out of Scope)
        this.fetchSalesData('payment', 'cash');
        this.fetchSalesData('payment', 'credit_card');
        this.fetchSalesData('payment', 'out_of_scope');

        // Get Credit (Returns): Cash and Credit Card (Out of Scope)
        this.fetchSalesData('credit', 'cash');
        this.fetchSalesData('credit', 'credit_card');
        this.fetchSalesData('credit', 'out_of_scope');
    }

    /**
     * Get Sales Summary data for a specific transaction and type
     *
     * @param transaction
     * @param type
     */
    fetchSalesData(transaction, type) {
        // Assemble the URI
        const params = {
            day: this.props.day,
            location_id: this.props.locationId,
            offset: this.props.offset,
            transaction: transaction,
            transaction_type: type
        };
        const resource = reportsResources.TransactionsByDay.GET_LIST(params);

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data.payload && result.data.payload.aggregations && result.data.payload.hits) {
                const aggregations = result.data.payload.aggregations;
                const count = result.data.payload.hits.total;
                this.processSalesData(transaction, type, aggregations, count);
            }
        }).catch((error) => {
            let message = Config.fuse.error.error + this.state.reportName;
            message += (transaction === 'credit') ? ' Returns.' : ' Payments.';
            if (error.response) {
                // Server response outside 2xx
                if (error.response.data && error.response.data.error) {
                    message = Config.fuse.error.error + this.state.reportName + ': ' + error.response.data.error;
                }
            } else if (error.request) {
                // No response
                message = Config.fuse.error.no_response + this.state.reportName + '.';
            } else {
                console.log('Error', error.message);
            }
            this.setState({
                summaryList: [],
                summaryTableData: [],
                summaryComplete: false,
            }, () => {
                if (typeof this.props.onError === 'function') {
                    this.props.onError(message);
                }
            });
        });
    }

    /* Data Processing ------------------------------ */

    /**
     * Store transaction/type results
     *
     * @param transaction
     * @param type
     * @param aggregations
     * @param hits
     */
    processSalesData(transaction, type, aggregations, hits) {
        let dataObj = {};

        if (type === 'cash') {
            dataObj = this.state.salesDataCash;
        }
        if (type === 'credit_card') {
            dataObj = this.state.salesDataCreditCard;
        }
        if (type === 'out_of_scope') {
            dataObj = this.state.salesDataOOS;
        }
        if (type === 'loyalty_points') {
            dataObj = this.state.salesDataPoints;
        }

        // Assemble subtotal+tax, subtotal, tax, and count
        const total = aggregations.agg_currency.value;
        const subtotal = aggregations.agg_subtotal.value;
        const tax = aggregations.agg_tax.value;
        const additionalTax = aggregations.agg_additional_tax.value;
        const totaltax = tax + additionalTax;
        const count = aggregations.transaction_count.value;

        // Populate the data object (Cash or Out of Scope)
        dataObj.date = this.props.day;
        dataObj.processed = false;
        dataObj.complete = false;
        if (type === 'cash') {
            dataObj.id = 'Cash';
            dataObj.payment = 'Cash';
        }
        if (type === 'credit_card' || type === 'out_of_scope') {
            dataObj.id = 'Credit Card';
            dataObj.payment = 'Credit Card';
        }

        /// Populate Payment columns
        if (transaction === 'payment') {
            dataObj.totalPayments = count;
            dataObj.saleTotal = total;
            dataObj.saleSubtotal = subtotal;
            dataObj.saleTax = totaltax;
        }

        // Populate Credit (Return) columns
        if (transaction === 'credit') {
            dataObj.totalReturns = count;
            dataObj.returnTotal = total;
            dataObj.returnSubtotal = subtotal;
            dataObj.returnTax = totaltax;
        }

        // Populate Net values if both totals exist
        if (dataObj.saleTotal !== undefined && dataObj.returnTotal !== undefined) {
            dataObj.net = (dataObj.saleTotal + dataObj.returnTotal);
            dataObj.processed = true;

            if (type === 'cash') {
                this.setState(
                    {
                        salesDataCash: dataObj
                    },
                    () => this.finalizeSalesData(transaction, type, dataObj)
                );
            }
            if (type === 'credit_card') {
                this.setState(
                    {
                        salesDataCreditCard: dataObj,
                    },
                    () => this.finalizeSalesData(transaction, type, dataObj)
                );
            }
            if (type === 'out_of_scope') {
                this.setState(
                    {
                        salesDataOOS: dataObj,
                    },
                    () => this.finalizeSalesData(transaction, type, dataObj)
                );
            }
            if (type === 'loyalty_points') {
                this.setState(
                    {
                        salesDataPoints: dataObj
                    },
                    () => this.finalizeSalesData(transaction, type, dataObj)
                );
            }

        }

        // Store the object

    }

    /**
     * Finalize and populate all columns for a row
     *
     * @param transaction
     * @param type
     * @param row
     */
    finalizeSalesData(transaction, type, row) {
        // Populate empty columns
        const emptyCurrency = toCurrency(0);
        row.totalPayments = (row.totalPayments) ? row.totalPayments : 0;
        row.totalReturns = (row.totalReturns) ? row.totalReturns : 0;
        row.saleTotal = (row.saleTotal) ? (toCurrency(row.saleTotal / 100)) : emptyCurrency;
        row.saleSubtotal = (row.saleSubtotal) ? (toCurrency(row.saleSubtotal / 100)) : emptyCurrency;
        row.saleTax = (row.saleTax) ? (toCurrency(row.saleTax / 100)) : emptyCurrency;
        row.returnTotal = (row.returnTotal) ? (toCurrency(row.returnTotal / 100)) : emptyCurrency;
        row.returnSubtotal = (row.returnSubtotal) ? (toCurrency(row.returnSubtotal / 100)) : emptyCurrency;
        row.returnTax = (row.returnTax) ? (toCurrency(row.returnTax / 100)) : emptyCurrency;
        row.net = (row.net) ? (toCurrency(row.net / 100)) : emptyCurrency;
        row.complete = true;

        if (type === 'cash') {
            this.setState(
                {
                    salesDataCash: row
                },
                () => this.generateSalesSummary()
            );
        }
        if (type === 'credit_card') {
            this.setState(
                {
                    salesDataCreditCard: row
                },
                () => this.generateSalesSummary()
            );
        }
        if (type === 'out_of_scope') {
            this.setState(
                {
                    salesDataOOS: row
                },
                () => this.generateSalesSummary()
            );
        }
        if (type === 'loyalty_points') {
            this.setState(
                {
                    salesDataPoints: row
                },
                () => this.generateSalesSummary()
            );
        }
    }

    /* Table Building ------------------------------ */

    /**
     * Assemble rows for Daily Sales by Day Summary table
     */
    generateSalesSummary() {

        // Retrieve all data objects (Cash, Credit Card, OOS)
        const rowCash = this.state.salesDataCash;
        const rowCC = this.state.salesDataCreditCard;
        const rowOOS = this.state.salesDataOOS;
        // const rowPoints = this.state.salesDataPoints;

        // If all individual types have been calculated:
        if (rowCash.complete && rowCC.complete && rowOOS.complete) {

            // Assemble combined Credit Card and OOS payments, returns and net
            const rowCCOOS = {};
            rowCCOOS.id = 'Credit Card';
            rowCCOOS.payment = 'Credit Card';
            rowCCOOS.date = this.props.day;
            rowCCOOS.totalPayments = rowCC.totalPayments + rowOOS.totalPayments;
            rowCCOOS.totalReturns = rowCC.totalReturns + rowOOS.totalReturns;
            rowCCOOS.saleTotal = toCurrency(parseCurrency(rowCC.saleTotal) + parseCurrency(rowOOS.saleTotal));
            rowCCOOS.saleSubtotal = toCurrency(parseCurrency(rowCC.saleSubtotal) + parseCurrency(rowOOS.saleSubtotal));
            rowCCOOS.saleTax = toCurrency(parseCurrency(rowCC.saleTax) + parseCurrency(rowOOS.saleTax));
            rowCCOOS.returnTotal = toCurrency(parseCurrency(rowCC.returnTotal) + parseCurrency(rowOOS.returnTotal));
            rowCCOOS.returnSubtotal = toCurrency(parseCurrency(rowCC.returnSubtotal) + parseCurrency(rowOOS.returnSubtotal));
            rowCCOOS.returnTax = toCurrency(parseCurrency(rowCC.returnTax) + parseCurrency(rowOOS.returnTax));
            rowCCOOS.net = toCurrency(parseCurrency(rowCC.net) + parseCurrency(rowOOS.net));

            let summaryList = [
                rowCash,
                rowCCOOS
            ];

            // Finalize Daily Sales Summary Table
            this.setState({
                salesSummaryList: summaryList,
                tableData: summaryList,
                summaryComplete: true
            });
            this.props.onSummaryComplete(true);
        }
    }

    /**
     * Update Footer display based on presence of filter
     *
     * @param event
     */
    handleFilterChange(event) {
        this.setState({
            displayTotals: !(event && event.length > 0)
        });
    }

    /**
     * Update current table data based on sort
     *
     * @param event
     */
    handleSortChange(event) {
        const tableData = getTableState(this.reactTable);
        if (tableData && tableData.length > 0) {
            this.setState({
                tableData: tableData
            });
        }
    }

    /**
     * Handle event to retrieve current state of table data
     *
     * @param event
     */
    handleExportClick = (event) => {
        // CSVLink handles generation and export of CSV
    };

    /* Table Display ------------------------------ */

    render() {

        const {
            day,
            locationId,
            system
        } = this.props;

        const {
            salesSummaryList,
            summaryComplete,
            tableData,
            tableHeaders
        } = this.state;

        const defaultSort = setDefaultTableSort('date');

        // ReactTable headers, columns and footer totals for Daily Sales Summary
        const salesColumns = [
            {
                Header: '',
                columns: [
                    {
                        Header: 'Date',
                        accessor: 'date',
                        headerClassName: 'th-min-110',
                        style: tableStyles.tdMin,
                        Footer: (<span style={tableStyles.footer}>&nbsp;</span>)
                    },
                    {
                        Header: 'Payment',
                        accessor: 'payment',
                        headerClassName: 'th-min-110',
                        style: tableStyles.tdMin,
                        headerStyle: tableStyles.thLast,
                        Footer: (<span style={tableStyles.footer}>Totals:</span>)
                    }
                ],
                headerClassName: 'th-min-220',
                style: tableStyles.thGroupMin
            },
            {
                Header: 'Totals',
                columns: [
                    {
                        Header: () => (<span>#<br/>Payments</span>),
                        accessor: 'totalPayments',
                        style: tableStyles.right,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {addCommas(addFromColumn(salesSummaryList, 'totalPayments'))}
                            </span>
                        )
                    },
                    {
                        Header: () => (<span>#<br/>Returns</span>),
                        accessor: 'totalReturns',
                        style: tableStyles.right,
                        headerStyle: tableStyles.thLast,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {addCommas(addFromColumn(salesSummaryList, 'totalReturns'))}
                            </span>
                        )
                    }
                ]
            },
            {
                Header: 'Sales',
                columns: [
                    {
                        Header: () => (<span>Gross<br/>Total</span>),
                        accessor: 'saleTotal',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        style: tableStyles.right,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {toCurrency(addFromColumn(salesSummaryList, 'saleTotal'))}
                            </span>
                        )
                    },
                    {
                        Header: 'Subtotal',
                        accessor: 'saleSubtotal',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        style: tableStyles.right,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {toCurrency(addFromColumn(salesSummaryList, 'saleSubtotal'))}
                            </span>
                        )
                    },
                    {
                        Header: 'Tax',
                        accessor: 'saleTax',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        style: tableStyles.right,
                        headerStyle: tableStyles.thLast,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {toCurrency(addFromColumn(salesSummaryList, 'saleTax'))}
                            </span>
                        )
                    }
                ]
            },
            {
                Header: 'Returns',
                columns: [
                    {
                        Header: () => (<span>Return<br/>Total</span>),
                        accessor: 'returnTotal',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        style: tableStyles.right,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {toCurrency(addFromColumn(salesSummaryList, 'returnTotal'))}
                            </span>
                        )
                    },
                    {
                        Header: () => (<span>Return<br/>Subtotal</span>),
                        accessor: 'returnSubtotal',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        style: tableStyles.right,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {toCurrency(addFromColumn(salesSummaryList, 'returnSubtotal'))}
                            </span>
                        )
                    },
                    {
                        Header: () => (<span>Return<br/>Tax</span>),
                        accessor: 'returnTax',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        style: tableStyles.right,
                        headerStyle: tableStyles.thLast,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {toCurrency(addFromColumn(salesSummaryList, 'returnTax'))}
                            </span>
                        )
                    }
                ]
            },
            {
                Header: 'Net',
                columns: [
                    {
                        Header: () => (
                            <span>
                                Adj. Net<br/>
                                <span style={tableStyles.thSmall}>(Gross Total - Return Total)</span>
                            </span>
                        ),
                        accessor: 'net',
                        style: tableStyles.right,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {toCurrency(addFromColumn(salesSummaryList, 'net'))}
                            </span>
                        )
                    }
                ]
            }
        ];

        return (
            <div>
                {summaryComplete &&
                <div className="reports-table-container">

                    {/* Table Header: Daily Sales by Day Summary */}
                    <div className="reports-table-title table-table-header">
                        <div className="btn-report-container report-header-button-container">
                            {/* Export Summary */}
                            <FlatButton
                                primary
                                type="button"
                                label=""
                                style={commonStyles.headerButton}
                            >
                                <ExportIcon style={commonStyles.headerButtonIcon}/>
                                <CSVLink
                                    data={tableData}
                                    headers={tableHeaders}
                                    filename={'Daily-Sales-Summary-' + system + '-' + locationId + '-' + day + '.csv'}
                                    onClick={(e) => this.handleExportClick(e)}
                                    target="_blank"
                                    style={commonStyles.headerButtonLink}
                                >
                                    Export Sales Summary
                                </CSVLink>
                            </FlatButton>
                        </div>
                        <h3 className="table-table-header-title">
                            <SalesSummaryIcon style={commonStyles.summaryIcon}/>
                            <span>Daily Sales Summary</span>
                        </h3>
                    </div>

                    {/* Table: Daily Sales Summary */}
                    <ReactTable
                        key="table-daily-sales-summary"
                        data={salesSummaryList}
                        columns={salesColumns}
                        defaultPageSize={1000}
                        showPagination={false}
                        defaultSorted={defaultSort}
                        onFilteredChange={this.handleFilterChange}
                        onSortedChange={this.handleSortChange}
                        ref={(r) => this.reactTable = r}
                        resizable={false}
                        minRows={2}
                        className="-striped"
                    />
                </div>
                }
            </div>
        )
    }
}

DailySalesByDayReport.propTypes = {
    addField: PropTypes.bool,
    day: PropTypes.string.isRequired,
    locationId: PropTypes.string.isRequired,
    offset: PropTypes.string.isRequired,
    onDisplayMessage: PropTypes.func,
    onError: PropTypes.func,
    onSummaryComplete: PropTypes.func,
    system: PropTypes.string,
};

DailySalesByDayReport.defaultProps = {
    addField: true,
    day: null,
    locationId: null,
    offset: null,
};

const enhance = compose(
    connect()
);

export default enhance(DailySalesByDayReport);



// WEBPACK FOOTER //
// ./src/Fuse/components/DailySalesByDayReport.js